import React, { useState } from "react";
import { Accordion, Button, Checkbox, Grid, Header, Icon, Input, Table } from "semantic-ui-react";
import { Flyout } from "../../../components/global_components/Flyout";
import { ListViewColumn } from "../../queries/getCrmCustomListViews";
import { ListFieldDefinition } from "../../queries/getCrmListFieldDefinitions";
import { faGripDotsVertical } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../../features/permits/components/ListSidebar.scss";

interface IColumnFlyoutProps {
  onClose: () => void;
  open?: boolean;
  columns: ListViewColumn[];
  fieldDefinitions: Record<string, ListFieldDefinition>;
  onSubmit: (data: ListViewColumn[]) => void;
  onViewSave?: (data: ListViewColumn[]) => void;
  shouldRenderSaveToViewButton?: boolean;
}

export const ColumnFlyout = ({
  open,
  onClose,
  columns,
  fieldDefinitions,
  onSubmit,
  onViewSave,
  shouldRenderSaveToViewButton,
}: IColumnFlyoutProps) => {
  const [currentColumns, setCurrentColumns] = useState(columns);

  const columnFieldDefinitions = Object.entries(fieldDefinitions).reduce(
    (acc, [fieldName, field]) => {
      if (field.isColumn) {
        acc[fieldName] = field;
      }
      return acc;
    },
    {} as Record<string, ListFieldDefinition>,
  );

  const displayColumns = currentColumns
    .reduce(
      (acc, column: ListViewColumn) => {
        const fieldDefinition =
          columnFieldDefinitions[column.fieldName] ||
          Object.values(columnFieldDefinitions).find((field) => field.fieldId === column.fieldId);
        if (fieldDefinition?.isColumn) {
          const label = fieldDefinition.label;
          acc.push({ ...column, label });
        }
        return acc;
      },
      [] as (ListViewColumn & { label: string })[],
    )
    .sort((a, b) => a.order - b.order);

  const columnFieldDefinitionGroups = Object.entries(columnFieldDefinitions).reduce(
    (acc, [fieldName, field]) => {
      const groupName = field.group || "Standard Columns";
      if (!acc[groupName]) {
        acc[groupName] = {};
      }
      acc[groupName][fieldName] = field;
      return acc;
    },
    {} as Record<string, Record<string, ListFieldDefinition>>,
  );

  const [activeGroups, setActiveGroups] = useState<string[]>(Object.keys(columnFieldDefinitionGroups));
  const [columnSearch, setColumnSearch] = useState("");

  const [currentDragIndex, setCurrentDragIndex] = useState<number | null>(null);
  const [currentDragHoverIndex, setCurrentDragHoverIndex] = useState<number | null>(null);

  const filteredColumnFieldDefinitionGroups = !columnSearch
    ? columnFieldDefinitionGroups
    : {
        ...Object.entries(columnFieldDefinitionGroups).reduce(
          (acc, [groupName, fields]) => {
            const filteredFields = Object.entries(fields).filter(([, field]) =>
              field.label.toLowerCase().includes(columnSearch.toLowerCase()),
            );
            if (filteredFields.length > 0) {
              acc[groupName] = Object.fromEntries(filteredFields);
            }
            return acc;
          },
          {} as Record<string, Record<string, ListFieldDefinition>>,
        ),
      };

  return (
    <>
      {" "}
      <Flyout open={open} onClose={onClose} contain={false} size='large'>
        <Flyout.Header>
          <div className='tw-flex tw-w-full tw-items-center tw-justify-between'>
            <div className='tw-flex'>
              <Icon className='button-icon' name='close' size='large' onClick={onClose} />
              <div>
                <h3>Choose Columns to Display</h3>
              </div>
            </div>
            <div className='tw-float-right'>
              <Button primary type='submit' onClick={() => onSubmit(currentColumns)}>
                {" "}
                Apply{" "}
              </Button>
              {shouldRenderSaveToViewButton && !!onViewSave && (
                <Button primary type='submit' onClick={() => onViewSave(currentColumns)}>
                  Save To View
                </Button>
              )}
            </div>
          </div>
        </Flyout.Header>
        <div className='tw-h-full tw-overflow-scroll tw-bg-[#F5F7F8]'>
          <Flyout.Body>
            <Grid columns={2} divided className='tw-p-[16px]'>
              <Grid.Column width={7} className='tw-pt-[4px]'>
                <Header as='h4' className='tw-mb-0' content='Active Columns' />
                <Table singleLine celled selectable className='columns-table'>
                  <Table.Body>
                    {displayColumns.map((column, index) => (
                      <Table.Row
                        key={index}
                        style={{ whiteSpace: "normal", textTransform: "capitalize" }}
                        className={`draggable ${
                          currentDragIndex && currentDragHoverIndex === index
                            ? currentDragHoverIndex > currentDragIndex
                              ? "bottom-drag"
                              : "top-drag"
                            : ""
                        }`}
                        draggable
                        onDragStart={(e: { dataTransfer: DataTransfer }) => {
                          const { dataTransfer } = e;
                          dataTransfer.setData("index", index.toString());
                          setCurrentDragIndex(index);
                        }}
                        onDragOver={(e: { preventDefault: () => {} }) => {
                          e.preventDefault();
                          if (index !== currentDragIndex) {
                            setCurrentDragHoverIndex(index);
                          }
                        }}
                        onDrop={(e: { dataTransfer: DataTransfer }) => {
                          const { dataTransfer } = e;
                          const dragIndex = Number(dataTransfer.getData("index"));
                          const hoverIndex = currentDragHoverIndex;
                          if (dragIndex !== null && hoverIndex !== null) {
                            setCurrentColumns((prev) => {
                              const newColumns = [...prev];
                              const dragColumn = newColumns[dragIndex];
                              newColumns.splice(dragIndex, 1);
                              newColumns.splice(hoverIndex, 0, dragColumn);
                              return newColumns.map((column, index) => ({
                                ...column,
                                order: index,
                              }));
                            });
                            setCurrentDragHoverIndex(null);
                            setCurrentDragIndex(null);
                          }
                        }}
                      >
                        <Table.Cell width={1} className='tw-border-l-[none]'>
                          <FontAwesomeIcon icon={faGripDotsVertical} size='xl' />
                        </Table.Cell>
                        <Table.Cell width={10}>{column.label}</Table.Cell>
                        <Table.Cell className='close-cell' width={1}>
                          <Icon
                            className='close-icon'
                            name='close'
                            onClick={() => {
                              setCurrentColumns((prev) =>
                                prev.filter(
                                  (c) =>
                                    c.fieldName !== column.fieldName || (!!c.fieldId && c.fieldId !== column.fieldId),
                                ),
                              );
                            }}
                          />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Grid.Column>
              <Grid.Column width={9} className='tw-pt-[4px]'>
                <p className='bold tw-mb-[4px]'>Filter Columns</p>
                <Input
                  className='tw-w-full'
                  onChange={(e) => {
                    setColumnSearch(e.target.value);
                  }}
                />
                <Accordion className='tw-mt-4 tw-border-none'>
                  {Object.entries(filteredColumnFieldDefinitionGroups).map(([groupName, fields], index: number) => {
                    return (
                      <React.Fragment key={index}>
                        <Accordion.Title
                          key={index}
                          className='tw-mb-0 tw-pb-0 tw-text-base tw-font-bold'
                          active={activeGroups.includes(groupName)}
                          onClick={() =>
                            setActiveGroups((prev) => {
                              if (prev.includes(groupName)) {
                                return prev.filter((group) => group !== groupName);
                              }
                              return [...prev, groupName];
                            })
                          }
                        >
                          <Icon name='dropdown' />
                          {groupName}
                        </Accordion.Title>
                        <Accordion.Content active={activeGroups.includes(groupName)}>
                          {Object.entries(fields).map(([fieldName, field], index: number) => {
                            return (
                              <div key={index} className='tw-flex tw-flex-col tw-gap-[16px]'>
                                <Checkbox
                                  checked={currentColumns.some(
                                    (column) =>
                                      column.fieldName === fieldName ||
                                      (column.fieldId && column.fieldId === field.fieldId),
                                  )}
                                  label={field.label}
                                  style={{ textTransform: "capitalize" }}
                                  className='column-checkbox tw-ml-6'
                                  onClick={() => {
                                    setCurrentColumns((prev) => {
                                      const isChecked = prev.some(
                                        (column) =>
                                          column.fieldName === fieldName ||
                                          (column.fieldId && column.fieldId === field.fieldId),
                                      );
                                      if (isChecked) {
                                        return prev.filter(
                                          (column) =>
                                            column.fieldName !== fieldName &&
                                            column.fieldId &&
                                            column.fieldId !== field.fieldId,
                                        );
                                      }
                                      return [
                                        ...prev,
                                        {
                                          fieldName: fieldName,
                                          fieldId: field.fieldId,
                                          fieldType: field.type,
                                          label: field.label,
                                          isColumn: true,
                                          order: prev.length,
                                        },
                                      ];
                                    });
                                  }}
                                />
                              </div>
                            );
                          })}
                        </Accordion.Content>
                      </React.Fragment>
                    );
                  })}
                </Accordion>
              </Grid.Column>
            </Grid>
          </Flyout.Body>
        </div>
      </Flyout>
    </>
  );
};
